<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="show"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
        :style="zIndex ? { zIndex } : undefined"
        @click.self="handleBackdropClick"
      >
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-black/15" v-if="closeOnBackdrop" @click="handleBackdropClick"></div>
        <div class="absolute inset-0 bg-black/15" v-else></div>

        <!-- Modal Content -->
        <Transition
          enter-active-class="transition-all duration-300 ease-out"
          enter-from-class="opacity-0 scale-95 translate-y-4"
          enter-to-class="opacity-100 scale-100 translate-y-0"
          leave-active-class="transition-all duration-200 ease-in"
          leave-from-class="opacity-100 scale-100 translate-y-0"
          leave-to-class="opacity-0 scale-95 translate-y-4"
        >
          <div
            v-if="show"
            :class="[
              'relative bg-white rounded-lg shadow-xl overflow-hidden',
              sizeClasses
            ]"
          >
            <!-- Header -->
            <div v-if="$slots.header || title || showCloseButton" :class="headerClasses" :style="headerStyle">
              <slot name="header">
                <h2 v-if="title" :class="titleClasses">
                  <span v-if="titleIcon" class="modal-title-icon">{{ titleIcon }}</span>
                  {{ title }}
                </h2>
                <div v-else></div>
              </slot>

              <button
                v-if="showCloseButton"
                @click="$emit('close')"
                :class="closeButtonClasses"
                :aria-label="closeButtonLabel"
              >
                <X class="w-5 h-5" />
              </button>
            </div>

            <!-- Body -->
            <div :class="bodyClasses">
              <slot>
                <div v-if="content" v-html="content"></div>
              </slot>
            </div>

            <!-- Footer -->
            <div v-if="$slots.footer || showDefaultFooter" :class="footerClasses">
              <slot name="footer">
                <div v-if="showDefaultFooter" class="flex justify-end gap-3 w-full">
                  <button
                    v-if="showCancelButton"
                    :id="cancelButtonId"
                    @click="$emit('cancel')"
                    class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  >
                    {{ cancelButtonText }}
                  </button>
                  <button
                    v-if="showConfirmButton"
                    :id="confirmButtonId"
                    @click="$emit('confirm')"
                    :disabled="confirmButtonDisabled"
                    :class="[
                      'modal-btn-confirm',
                      confirmButtonVariantClass
                    ]"
                  >
                    {{ confirmButtonText }}
                  </button>
                </div>
              </slot>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
defineOptions({
  name: 'ModalComponent',
});

import { computed, onMounted, onUnmounted } from 'vue'
import { X } from '@lucide/vue'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    default: ''
  },
  titleIcon: {
    type: String,
    default: ''
  },
  content: {
    type: String,
    default: ''
  },
  size: {
    type: String,
    default: 'md',
    validator: (value) => ['xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl', 'wide', 'full'].includes(value)
  },
  theme: {
    type: String,
    default: 'default',
    validator: (value) => ['default', 'orange', 'blue', 'pink', 'green'].includes(value)
  },
  showCloseButton: {
    type: Boolean,
    default: true
  },
  closeButtonLabel: {
    type: String,
    default: 'Sluiten'
  },
  closeOnBackdrop: {
    type: Boolean,
    default: false
  },
  closeOnEscape: {
    type: Boolean,
    default: true
  },
  showDefaultFooter: {
    type: Boolean,
    default: false
  },
  showCancelButton: {
    type: Boolean,
    default: true
  },
  showConfirmButton: {
    type: Boolean,
    default: true
  },
  cancelButtonText: {
    type: String,
    default: 'Annuleren'
  },
  confirmButtonText: {
    type: String,
    default: 'Bevestigen'
  },
  confirmButtonVariant: {
    type: String,
    default: 'primary',
    validator: (value) => ['primary', 'danger', 'success', 'warning', 'info', 'orange', 'blue', 'pink', 'green'].includes(value)
  },
  bodyPadding: {
    type: Boolean,
    default: true
  },
  cancelButtonId: {
    type: String,
    default: 'modal-cancel-button'
  },
  confirmButtonId: {
    type: String,
    default: 'modal-confirm-button'
  },
  confirmButtonDisabled: {
    type: Boolean,
    default: false
  },
  zIndex: {
    type: Number,
    default: null
  }
})

const emit = defineEmits(['close', 'cancel', 'confirm', 'select'])

// Computed properties for styling
const sizeClasses = computed(() => {
  const sizes = {
    xs: 'w-full max-w-xs',
    sm: 'w-full max-w-sm',
    md: 'w-full max-w-md',
    lg: 'w-full max-w-lg',
    xl: 'w-full max-w-xl',
    '2xl': 'w-full max-w-2xl',
    '3xl': 'w-full max-w-3xl',
    '4xl': 'w-full max-w-4xl',
    wide: 'w-full max-w-[76rem]',
    full: 'w-full max-w-[95vw]'
  }
  return `${sizes[props.size] || sizes.md} max-h-[calc(100vh-2rem)] flex flex-col`
})

const bodyClasses = computed(() => {
  const padding = props.bodyPadding ? 'p-6' : ''
  const themeClasses = ''
  return `${padding} ${themeClasses} flex-1 min-h-0 overflow-x-hidden overflow-y-auto`.trim()
})

const headerClasses = computed(() => {
  const base = 'flex items-center justify-between p-6 border-b text-white'
  const borderMap = {
    default: 'border-gray-200',
    orange: 'border-orange-600',
    blue: 'border-blue-600',
    pink: 'border-pink-600',
    green: 'border-green-600'
  }
  const textMap = {
    default: 'text-gray-900',
    orange: 'text-white',
    blue: 'text-white',
    pink: 'text-white',
    green: 'text-white'
  }
  return `${base} ${borderMap[props.theme]} ${textMap[props.theme]}`
})

const headerStyle = computed(() => {
  const gradientMap = {
    default: '',
    orange: 'linear-gradient(45deg, #df4f0e, #dc3329)',
    blue: 'linear-gradient(45deg, #0077cc, #0081cf)',
    pink: 'linear-gradient(45deg, #d60060, #e80068)',
    green: 'linear-gradient(45deg, #0ea58c, #10b981)'
  }
  const gradient = gradientMap[props.theme]
  return gradient ? `background: ${gradient};` : ''
})

const titleClasses = computed(() => {
  if (props.theme === 'default') {
    return 'text-xl font-semibold text-gray-900'
  }
  return 'text-xl font-semibold text-white opacity-95'
})

const closeButtonClasses = computed(() => {
  if (props.theme === 'default') {
    return 'text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100'
  }
  return 'text-white hover:text-gray-200 transition-colors p-1 rounded-full hover:bg-white hover:bg-opacity-20'
})

const footerClasses = computed(() => {
  const base = 'flex justify-end gap-3 p-6 border-t'
  const borderMap = {
    default: 'border-gray-200 bg-gray-50',
    orange: 'border-orange-600 bg-orange-50',
    blue: 'border-blue-600 bg-blue-50',
    pink: 'border-pink-600 bg-pink-50',
    green: 'border-green-600 bg-green-50'
  }
  return `${base} ${borderMap[props.theme]}`
})

const confirmButtonVariantClass = computed(() => {
  // Returns modifier class for the variant
  const variantMap = {
    primary: 'modal-btn-confirm-primary',
    danger: 'modal-btn-confirm-danger',
    success: 'modal-btn-confirm-success',
    warning: 'modal-btn-confirm-warning',
    info: 'modal-btn-confirm-info',
    orange: 'modal-btn-confirm-orange',
    blue: 'modal-btn-confirm-blue',
    pink: 'modal-btn-confirm-pink',
    green: 'modal-btn-confirm-green',
    red: 'modal-btn-confirm-danger'
  }
  return variantMap[props.confirmButtonVariant] || ''
})

// Handle backdrop click
const handleBackdropClick = () => {
  if (props.closeOnBackdrop) {
    emit('close')
  }
}

// Handle escape key
const handleEscape = (event) => {
  if (event.key === 'Escape' && props.closeOnEscape && props.show) {
    emit('close')
  }
}

// Lifecycle hooks
onMounted(() => {
  document.addEventListener('keydown', handleEscape)
  // Prevent body scroll when modal is open
  if (props.show) {
    document.body.style.overflow = 'hidden'
  }
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleEscape)
  // Restore body scroll
  document.body.style.overflow = ''
})

// Watch for show prop changes to handle body scroll
import { watch } from 'vue'
watch(() => props.show, (newValue) => {
  if (newValue) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
})
</script>
